import React, { useCallback } from 'react';
import styled from 'styled-components';
import { layout } from '@prisjakt/ui-base';

import { useAppContext } from '@prisjakt/ui-app-context';
import { trackProductCollectionWordCloudClick } from 'src/areas/product-collection/tracking';

import * as S from './styles';
import {
  TonalButton,
  ButtonText,
  ButtonIcon,
  ButtonSize,
} from '@prisjakt/ui/button/variants';
import Clickable from '@prisjakt/ui-clickable';
import { IconMagnifier } from '@prisjakt/ui/icon';
import { Behavior } from '@prisjakt/ui/text';

type WordCloudProps = {
  title: string;
  items: WordCloudItem[];
  showMoreUrl?: string;
  visibleItems?: number;
};

type WordCloudItem = {
  name: string;
  url: string;
};

const WordCloud = ({
  title,
  items,
  showMoreUrl,
  visibleItems,
}: WordCloudProps) => {
  const { t } = useAppContext();
  const onClick = useCallback((event: React.MouseEvent) => {
    const href = event.currentTarget.getAttribute('href');
    trackProductCollectionWordCloudClick(href as string);
  }, []);

  return (
    <S.WordCloudContainer>
      <S.WordCloudH3>{title}</S.WordCloudH3>

      <List>
        {(visibleItems ? items.slice(0, visibleItems) : items).map(
          ({ name, url }) => (
            <ListItem key={url}>
              <TonalButton
                size={ButtonSize.SMALL}
                onClick={onClick}
                container={Clickable as any}
                containerProps={{
                  to: url,
                  title: name,
                }}
                fullWidth
              >
                <ButtonIcon>
                  <IconMagnifier />
                </ButtonIcon>
                <ButtonText behavior={Behavior.OnelineEllipsis}>
                  {name}
                </ButtonText>
              </TonalButton>
            </ListItem>
          )
        )}

        {showMoreUrl && (
          <S.ShowMoreButton>
            <TonalButton
              container={Clickable as any}
              containerProps={{ to: showMoreUrl }}
              onClick={onClick}
              size={ButtonSize.SMALL}
            >
              <ButtonText>{t('common', 'button.show-more')}</ButtonText>
              <ButtonIcon>
                <S.ShowMoreIcon />
              </ButtonIcon>
            </TonalButton>
          </S.ShowMoreButton>
        )}
      </List>
    </S.WordCloudContainer>
  );
};

export const List = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
`;

const ListItem = styled.li`
  margin: ${layout.measurements.spacing025};
  overflow: hidden;
  max-width: 100%;
`;

export default WordCloud;
